import {
	Box,
	IconButton,
	Pagination,
	Stack,
	Typography,
	Chip,
	Divider,
	Switch,
	TextField,
	Button,
} from '@mui/material';
import CustomButton from '@src/components/CustomButton/CustomButton';
import CustomDataGrid2 from '@src/components/CustomDataGrid/CustomDataGrid2';
import { IsMobileWidth, IsTabletWidth } from '@src/components/MediaQueryUtils';
import PageHeader from '@src/components/PageHeader/PageHeader';
import SearchBar from '@src/components/SearchBar';
import { render } from '@testing-library/react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import ChatIcon from '@mui/icons-material/Chat';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import CustomAlert from '@src/components/CustomAlert/CustomAlert';
import { formatPriceValue, roundTo2Decimals } from '@src/helpers/Helpers';
import CustomDrawer from '@src/components/CustomDrawer/CustomDrawer';
import ViewOrder from '@src/components/OrderView/ViewOrder';
import { useAppSelector } from '@src/store/hooks';
import { AxiosError } from 'axios';
import CustomDialog from '@src/components/CustomDialog';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	background-color: var(--chat-header-bg);
	transition: width 0.3s ease;
`;

const ExportButtonStyles = styled('div')({
	'@media (max-width: 1024px)': {
		marginLeft: '16px',
	},
	'@media (max-width: 478px)': {
		background: 'white',
		display: 'flex',
		marginLeft: '0px',
		position: 'fixed',
		bottom: 0,
		zIndex: '100',
		width: '100%',
		padding: '16px',
		left: 0,
		borderRadius: '6px 6px 0px 0px',
		borderTop: '1.5px solid #F1F1F1',
	},
});

const getColumns = (onAddAmount: any, onHistoryPress: any, onMaskSwitch: any) => [
	{
		field: 'organization_name',
		headerName: 'Dealer',
		width: 200,
		renderCell: (params: any) => (
			<Stack direction="row" alignItems={'center'} gap={1}>
				<img
					src={params?.row?.image || '/Cheeni-fallback.svg'}
					alt="organization-logo"
					width="30px"
					height="30px"
					style={{ borderRadius: '50%' }}
				/>
				{params?.row?.name}
			</Stack>
		),
	},
	{
		field: 'name',
		headerName: 'Contact Name',
		width: 200,
		renderCell: (params: any) => (
			<div>{params?.row?.customers?.firstname + ' ' + params?.row?.customers?.lastname}</div>
		),
	},
	{
		field: 'contact_no',
		headerName: 'Contact No',
		width: 200,
		renderCell: (params: any) => <div>{params?.row?.customers?.telephone}</div>,
	},
	{
		field: 'availableBonusAmount',
		headerName: 'Available Bonus Amount',
		width: 200,
		renderCell: (params: any) => <div>Rs {params?.row?.availableBonusAmount}</div>,
	},
	{
		field: 'totalWalletBonus',
		headerName: 'Total Bonus Credited',
		width: 200,
		renderCell: (params: any) => <div>Rs {params?.row?.totalWalletBonus}</div>,
	},
	{
		field: 'actions',
		headerName: 'Actions',
		width: 300,
		renderCell: (params: any) => {
			return (
				<Stack direction={'row'} gap={1} alignItems={'center'}>
					<Button
						variant="contained"
						color="primary"
						sx={{
							height: '34px !important',
						}}
						onClick={(e) => onHistoryPress(e, params.row)}
					>
						History
					</Button>
					<Button
						variant="contained"
						color="primary"
						sx={{
							height: '34px !important',
						}}
						onClick={(e) => onAddAmount(e, params.row)}
					>
						Add Amount
					</Button>
				</Stack>
			);
		},
	},
];

const ordersColumns = [
	{
		field: 'timeId',
		headerName: 'Order ID',
		width: 200,
		// headerAlign: 'center',
		// align: 'center',
	},
	{ field: 'orderDate', headerName: 'Ordered', width: 230 },
	{ field: 'deliveryDate', headerName: 'Delivery Date', width: 200 },
	{
		field: 'statusData',
		headerName: 'Order Status',
		width: 168,
		renderCell: (params: any) => (
			<Chip
				label={params.row?.orderStatus?.name}
				style={{
					backgroundColor: params.row?.orderStatus?.color_bg,
					color: params?.row?.orderStatus?.color,
					fontWeight: 'bold',
					borderRadius: '4px',
				}}
			/>
		),
	},
	{
		field: 'paymentStatus',
		headerName: 'Payment Status',
		width: 168,
		renderCell: (params: any) => {
			if (!params.row?.paymentStatus?.name) {
				return null;
			}
			const bgMap: any = {
				Paid: {
					color: '#00BFA5',
					color_bg: '#E0F3F2',
				},
				'Partially Paid': {
					color: '#FFC107',
					color_bg: '#FFF3E0',
				},
				Unpaid: {
					color: '#F44336',
					color_bg: '#FFEBEE',
				},
				Overdue: {
					color: '#F44336',
					color_bg: '#FFEBEE',
				},
			};
			const name = params.row?.paymentStatus?.name;
			const color = bgMap[name]?.color;
			const color_bg = bgMap[name]?.color_bg;
			return (
				<Chip
					label={name}
					style={{
						backgroundColor: color_bg,
						color: color,
						fontWeight: 'bold',
						borderRadius: '4px',
					}}
				/>
			);
		},
	},
	{
		field: 'creditStatus',
		headerName: 'Credit Status',
		width: 168,
		renderCell: (params: any) => (
			<Chip
				label={params.row?.creditStatus?.split('_').join(' ') || ''}
				style={{
					// backgroundColor: params.row?.statusData?.color_bg,
					color: params?.row?.creditStatus === 'AVAILABLE' ? 'green' : 'red',
					fontWeight: 'bold',
					borderRadius: '4px',
				}}
			/>
		),
	},
];

function RenderOrderDetails({
	orderDetails,
	setOrderDetails,
	orderInfo,
	setOrderInfo,
	currentUser,
	alert,
	setAlert,
	displayCustomError,
	viewOrder,
	setViewOrder,
}: any) {
	const isMobileWidth = IsMobileWidth();
	if (orderDetails) {
		return (
			<CustomDrawer
				open={viewOrder}
				setOpen={setViewOrder}
				style={{ width: isMobileWidth ? '100%' : '80%' }}
				header={false}
			>
				<ViewOrder
					alert={alert}
					setAlert={setAlert}
					orderDetails={orderDetails}
					setOrderDetails={setOrderDetails}
					orderInfo={orderInfo}
					setOrderInfo={setOrderInfo}
					currentUser={currentUser}
					title={orderDetails.firstname}
					handleClose={() => setViewOrder(false)}
					displayCustomError={displayCustomError}
					// setApiCall={setApiCall}
					// isOrderPage={true}
				/>
			</CustomDrawer>
		);
	}
	return null;
}

function RenderCustomerWalletHistory({
	showAllCustomerOrders,
	customerWalletHistory,
	setShowAllCustomerOrders,
	currentCustomer,
	alert,
	setAlert,
	loadingHistory,
}: any) {
	const TimelineItem = styled.div`
		display: flex;
		position: relative;
		padding: 20px 0;
		&:not(:last-child)::before {
			content: '';
			position: absolute;
			left: 9px;
			top: 50px;
			height: calc(100% - 20px);
			width: 2px;
			background-color: #e0e0e0;
		}
	`;

	const TimelineDot = styled.div<{ status: string }>`
		width: 20px;
		height: 20px;
		border-radius: 50%;
		background-color: ${(props) => {
			const statusColorMap: { [key: string]: string } = {
				PENDING: '#FFC107', // Yellow
				AVAILABLE: '#4CAF50', // Green
				EXPIRED: '#F44336', // Red
				REDEEMED: '#9E9E9E', // Gray
				PARTIALLY_REDEEMED: '#2196F3', // Blue
			};
			return statusColorMap[props.status] || '#9E9E9E';
		}};
		margin-right: 20px;
		position: relative;
		z-index: 1;
		margin-top: 10px;
	`;

	const TimelineContent = styled.div`
		flex: 1;
	`;

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const getStatusColor = (status: string) => {
		const statusColorMap: { [key: string]: { bg: string; text: string } } = {
			PENDING: {
				bg: '#FFF3E0',
				text: '#E65100',
			},
			AVAILABLE: {
				bg: '#E8F5E9',
				text: '#2E7D32',
			},
			EXPIRED: {
				bg: '#FFEBEE',
				text: '#C62828',
			},
			REDEEMED: {
				bg: '#F5F5F5',
				text: '#616161',
			},
			PARTIALLY_REDEEMED: {
				bg: '#E3F2FD',
				text: '#1565C0',
			},
		};
		return statusColorMap[status] || { bg: '#F5F5F5', text: '#616161' };
	};

	return (
		<>
			<CustomDrawer
				open={showAllCustomerOrders}
				style={{ width: IsMobileWidth() ? '90%' : '30%' }}
				setOpen={setShowAllCustomerOrders}
				header={false}
			>
				<Box m={1}>
					<Typography sx={{ mt: 2, mb: 2 }} fontSize={20}>
						Wallet History for {currentCustomer?.name}
					</Typography>
					<Divider sx={{ mb: 3 }} />
					{loadingHistory ? (
						<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
							<Typography>Loading history...</Typography>
						</Box>
					) : customerWalletHistory?.length === 0 ? (
						<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
							<Typography>No history available</Typography>
						</Box>
					) : (
						customerWalletHistory?.map((item: any) => {
							const statusColors = getStatusColor(item.status);
							return (
								<TimelineItem key={item.id}>
									<TimelineDot status={item.status} />
									<TimelineContent>
										<Stack direction="row" justifyContent="space-between" alignItems="center">
											<Typography variant="subtitle1" fontWeight="bold">
												Rs. {item.amount}
											</Typography>
											<Chip
												label={item.status}
												sx={{
													backgroundColor: statusColors.bg,
													color: statusColors.text,
													fontWeight: 'medium',
												}}
											/>
										</Stack>
										<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
											{formatDate(item.createdAt)}
										</Typography>
									</TimelineContent>
								</TimelineItem>
							);
						})
					)}
				</Box>
			</CustomDrawer>
		</>
	);
}

function AddWalletAmountModal({ open, setOpen, selectedCustomers, onSave }: any) {
	const [amount, setAmount] = useState('');
	const [error, setError] = useState('');

	const handleSave = () => {
		if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
			setError('Please enter a valid amount');
			return;
		}
		if (Number(amount) > 100000) {
			setError('Please enter an amount less than 100000');
			return;
		}

		onSave(Number(amount));
		setAmount('');
		setError('');
	};

	return (
		<CustomDialog
			open={open}
			setOpen={setOpen}
			disable={false}
			title="Add Amount to Wallet"
			description={
				<Box sx={{ mt: 2 }}>
					<Typography variant="subtitle1" sx={{ mb: 2 }}>
						Selected Customers:
					</Typography>
					{selectedCustomers.map((customer: any) => (
						<Typography key={customer.id} sx={{ mb: 1 }}>
							{customer.name}
						</Typography>
					))}
					<TextField
						fullWidth
						label="Amount"
						// type="number"
						value={amount}
						onChange={(e) => {
							setAmount(e.target.value);
							setError('');
						}}
						error={!!error}
						helperText={error}
						sx={{ mt: 2 }}
					/>
				</Box>
			}
			handleClose={() => {
				setOpen(false);
				setAmount('');
				setError('');
			}}
			handleYes={handleSave}
		/>
	);
}

function CustomerWallet() {
	const { id } = useParams();
	const navigate = useNavigate();
	const isMobile = IsMobileWidth();
	const [searchValue, setSearchValue] = useState('');
	const [totalPages, setTotalPages] = useState(1);
	const [page, setPage] = useState(1);
	const [searching, setSearching] = useState(false);
	const [showAllCustomerOrders, setShowAllCustomerOrders] = useState(false);
	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
	const [shareModal, setShareModal] = useState(false);
	const [loadingCustomerList, setLoadingCustomerList] = useState<boolean>(false);
	const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
	const [customerList, setCustomerList] = useState([]);
	const { apiService }: any = React.useContext(ApplicationContext);
	const [customerWalletHistory, setCustomerWalletHistory] = useState([]);
	const [currentCustomer, setCurrentCustomer] = useState({});
	const [customerToMask, setCustomerToMask] = useState({
		selectedIds: [],
		state: false,
	});
	const [alert, setAlert] = useState({
		message: '',
		severity: 'success',
	});
	const limit = 25;
	const [showAddWalletModal, setShowAddWalletModal] = useState(false);
	const [selectedCustomers, setSelectedCustomers] = useState([]);

	let params = {
		limit: limit,
		page: page,
		search: searchValue,
	};

	const resetPriceMask = () => {
		setCustomerToMask({
			selectedIds: [],
			state: false,
		});
	};

	const onAddAmount = (e: any, data: any) => {
		e.preventDefault();
		console.log(data);
		setSelectedCustomers([data]);
		setShowAddWalletModal(true);
	};

	const onHistoryPress = (e: any, data: any) => {
		e.preventDefault();
		console.log(data);
		setCurrentCustomer(data);
		fetchCustomerWalletHistory(data?.id);
		setShowAllCustomerOrders(true);
	};

	const onMaskSwitch = (e: any, data: any, status: any) => {
		e.preventDefault();
		console.log(data, status);
		const ids = data.map((item: any) => item?.id);
		setCustomerToMask({
			selectedIds: ids,
			state: status,
		});
	};

	const columns = getColumns(onAddAmount, onHistoryPress, onMaskSwitch);
	const handleSearch = (value: any) => {
		setSearching(true);
		setSearchValue(value);
		setPage(1);
	};

	const handlePageChange = (event: any, value: number) => {
		if (page !== value) {
			setPage(value);
			setSelectedItemIds([]);
			setLoadingCustomerList(true);
		}
	};

	const onRowsSelectionHandler = (ids: string[]) => {
		setSelectedItemIds(ids);
		const selected = customerList.filter((customer: any) => ids.includes(customer.id));
		setSelectedCustomers(selected);
	};

	useEffect(() => {
		fetchCustomerList();
	}, [searchValue, page]);

	function fetchCustomerList() {
		setLoadingCustomerList(true);
		apiService.getCustomerWallet(
			(response: any) => {
				console.log(response);
				const data = response?.data?.data;
				const { totalCount, customers } = data || {};
				setCustomerList(customers);
				setTotalPages(Math.ceil(totalCount / limit));
				setLoadingCustomerList(false);
			},
			(error: any) => {
				console.log(error);
				setAlert({ message: error.response.data.message, severity: 'error' });
				setLoadingCustomerList(false);
			},
			id,
			params
		);
	}

	function fetchCustomerWalletHistory(customerId: any) {
		setLoadingHistory(true);
		apiService.getCustomerWalletHistory(
			(response: any) => {
				console.log(response);
				const history = response?.data?.data?.history;
				setCustomerWalletHistory(history);
				setLoadingHistory(false);
			},
			(error: any) => {
				console.log(error);
				setAlert({ message: error.response.data.message, severity: 'error' });
				setLoadingHistory(false);
			},
			id,
			{
				id: customerId,
			}
		);
	}

	useEffect(() => {
		fetchCustomerList();
	}, []);

	const handleAddWalletAmount = (amount: number) => {
		console.log('Adding amount:', amount, 'to customers:', selectedCustomers);

		apiService.addBonusToWalletForCustomers(
			{
				customers: selectedCustomers?.map((customer: any) => customer?.id),
				amount: amount,
			},
			(response: any) => {
				fetchCustomerList();
				setShowAddWalletModal(false);
				setAlert({ message: 'Amount added to wallet successfully', severity: 'success' });
			},
			(error: any) => {
				console.log(error);
				setAlert({ message: error.response.data.message, severity: 'error' });
				setShowAddWalletModal(false);
			},
			id
		);
	};

	return (
		<StyledContainer>
			<PageHeader title={'Customers Wallet'} />
			<Stack p={2} direction="row" justifyContent="space-between">
				<SearchBar
					style={{
						width: IsTabletWidth() ? '100%' : '312px',
						height: '36px',
					}}
					value={searchValue}
					onChange={handleSearch}
					isLoading={false}
				/>
				{selectedItemIds.length > 0 && (
					<Stack direction="row" gap={1}>
						<CustomButton
							title="Add Amount to Wallet"
							color="primary"
							styles={{
								height: '36px',
								'@media (max-width: 478px)': {
									width: '100%',
								},
							}}
							handleClick={() => setShowAddWalletModal(true)}
						/>
					</Stack>
				)}
			</Stack>
			<Box
				m={1}
				sx={{ width: isMobile ? 'calc(100vw - 10px)' : 'calc(100vw - 258px)', overflowX: 'auto' }}
			>
				<CustomDataGrid2
					rows={customerList}
					columns={columns}
					checkboxSelection={true}
					style={{
						transition: 'height 0.3s ease',
						maxHeight: 'calc(100vh - 250px)',
						minHeight: 'calc(100vh - 250px)',
					}}
					// showNoRowsFound={!orders?.length}
					onRowsSelectionHandler={onRowsSelectionHandler}
					handleRowClick={() => {}}
					// responsiveStyles={gridResponsiveStyles}
					// loading={loadingHistory}
					type="server"
					filterType="server"
				/>
				<Stack alignItems={'center'} mt={1}>
					<Pagination color="primary" count={totalPages} page={page} onChange={handlePageChange} />
				</Stack>
			</Box>
			<RenderCustomerWalletHistory
				showAllCustomerOrders={showAllCustomerOrders}
				setShowAllCustomerOrders={setShowAllCustomerOrders}
				currentCustomer={currentCustomer}
				customerWalletHistory={customerWalletHistory}
				{...{ alert, setAlert, loadingHistory }}
			/>
			{alert?.message !== '' ? <CustomAlert alert={alert} setAlert={setAlert} /> : null}
			<AddWalletAmountModal
				open={showAddWalletModal}
				setOpen={setShowAddWalletModal}
				selectedCustomers={selectedCustomers}
				onSave={handleAddWalletAmount}
			/>
		</StyledContainer>
	);
}

export default CustomerWallet;
